import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Animated,
  Modal,
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  View,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppScreen } from '../../components/AppScreen';
import { ConfigurationDeleteButton } from '../../components/ConfigurationDeleteButton';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { getProjectModuleCapabilities } from '../../config/projectCapabilities';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import {
  deleteProjectTask,
  getCompletedFocusTasks,
  getProjectBoard,
  moveProjectTask,
  reorderProjectSubtasks,
} from '../../services/projectService';
import type {
  CompletedFocusBoard,
  ProjectBoard,
  ProjectBoardColumn,
  ProjectSubtask,
  ProjectTask,
} from '../../types/project';
import {
  formatCalendarDate,
  formatMinutes,
  todayBusinessCalendarDate,
} from '../../utils/dateTime';
import { projectErrorMessage } from '../../utils/projectErrors';
import { formatColumnTaskCount, isUnlimitedTaskLimit } from '../../utils/projectForm';
import {
  formatCompletedCardDate,
  formatCompletedFocusDate,
  groupTasksByCompletedDate,
  shiftCompletedFocusDate,
} from '../../utils/taskCompletionDate';
import {
  assigneeSummary,
  boardColumnWidth,
  formatDurationInput,
  planTaskMove,
  relocateTaskOnBoard,
  removeTaskFromBoard,
} from '../../utils/projectTaskForm';
import {
  FOCUS_TIMER_PRESETS,
  type FocusTimerMode,
} from '../../utils/taskFocusTimer';
import {
  useTaskFocusTimer,
  type TaskFocusTimerView,
} from '../../hooks/useTaskFocusTimer';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { CompleteTaskModal } from './CompleteTaskModal';
import { CreateTaskModal } from './CreateTaskModal';
import { TaskDetailsPanel } from './TaskDetailsPanel';
import { OperationFeedbackHost } from '../../components/OperationFeedbackHost';
import { runOperation } from '../../utils/operationFeedback';

type Props = NativeStackScreenProps<RootStackParamList, 'ProjectTaskBoard'>;

/** Pointer travel (px) that turns a press into a drag; shorter is a tap. */
const DRAG_START_DISTANCE = 6;
const GHOST_OFFSET = { x: 80, y: 24 };

function EyeIcon({ color, active }: { color: string; active: boolean }) {
  return (
    <View style={styles.eyeRoot} accessibilityElementsHidden>
      <View style={[styles.eyeLid, { borderColor: color }]} />
      <View style={[styles.eyePupil, { backgroundColor: color }]} />
      {active ? (
        <View style={[styles.eyeSlash, { backgroundColor: color }]} />
      ) : null}
    </View>
  );
}

type DisplayColumn = ProjectBoardColumn & {
  reviewLane?: 'today' | 'yesterday';
};

type LayoutBox = { x: number; y: number; width: number; height: number };
type DragState =
  | { kind: 'task'; task: ProjectTask }
  | { kind: 'subtask'; task: ProjectTask; subtask: ProjectSubtask };

type PointerEventLike = {
  nativeEvent: { pageX: number; pageY: number; button?: number };
};

type BoardColumnView = View & { _boardColumnId?: number };

/** Block browser/OS context menus so they never interrupt a task drag. */
function suppressDragMenu(event?: {
  preventDefault?: () => void;
  stopPropagation?: () => void;
}) {
  event?.preventDefault?.();
  event?.stopPropagation?.();
}

const dragSafeTextStyle = Platform.select<StyleProp<TextStyle>>({
  web: {
    // RN Web passes these through to the DOM to stop selection callouts.
    userSelect: 'none',
    WebkitUserSelect: 'none',
    WebkitTouchCallout: 'none',
  } as TextStyle,
  default: null,
});

const dragSafeCardStyle = Platform.select<StyleProp<ViewStyle>>({
  web: {
    userSelect: 'none',
    WebkitUserSelect: 'none',
    WebkitTouchCallout: 'none',
    touchAction: 'none',
  } as ViewStyle,
  default: null,
});

function TaskCard({
  task,
  titleColor,
  metaColor,
  borderColor,
  surfaceColor,
  actionColor,
  canDrag,
  canDelete,
  highlighted,
  dragging,
  activeTimer,
  onOpen,
  onDelete,
  onDragTask,
  onDragMove,
  onDragEnd,
  onDragSubtask,
  onPressActiveChange,
  onSubtaskRef,
  onStartTimer,
  onStopTimer,
}: {
  task: ProjectTask;
  titleColor: string;
  metaColor: string;
  borderColor: string;
  surfaceColor: string;
  actionColor: string;
  canDrag: boolean;
  canDelete: boolean;
  highlighted: boolean;
  dragging: boolean;
  activeTimer: TaskFocusTimerView | null;
  onOpen: () => void;
  onDelete: () => void;
  onDragTask: (pageX: number, pageY: number) => void;
  onDragMove: (pageX: number, pageY: number) => void;
  onDragEnd: (pageX: number, pageY: number) => void;
  onDragSubtask: (subtask: ProjectSubtask, pageX: number, pageY: number) => void;
  /** Native only: a draggable press is in progress, so parents stop scrolling. */
  onPressActiveChange: (active: boolean) => void;
  onSubtaskRef: (
    subtaskId: number,
    view: View | null,
    taskId: number,
  ) => void;
  onStartTimer: (mode: FocusTimerMode) => void;
  onStopTimer: () => void;
}) {
  const members = assigneeSummary(task.assignees, task.assignedEmployeeName);
  const subtasks = [...task.subtasks].sort(
    (left, right) => left.displayOrder - right.displayOrder,
  );
  const timerForThisTask =
    activeTimer?.taskId === task.taskId ? activeTimer : null;

  // A press becomes a drag as soon as it moves past DRAG_START_DISTANCE, so
  // tasks drag immediately while a plain tap still opens the details.
  const pressStart = useRef<{
    pageX: number;
    pageY: number;
    subtask: ProjectSubtask | null;
  } | null>(null);
  const pressedSubtask = useRef<ProjectSubtask | null>(null);
  const dragActive = useRef(false);
  const suppressPress = useRef(false);

  function handlePointerStart(pageX: number, pageY: number): boolean {
    const subtask = pressedSubtask.current;
    pressedSubtask.current = null;
    suppressPress.current = false;
    dragActive.current = false;
    pressStart.current = canDrag ? { pageX, pageY, subtask } : null;
    return pressStart.current != null;
  }

  function handlePointerMove(pageX: number, pageY: number) {
    if (dragActive.current) {
      onDragMove(pageX, pageY);
      return;
    }
    const start = pressStart.current;
    if (!start) return;
    const distance = Math.hypot(pageX - start.pageX, pageY - start.pageY);
    if (distance < DRAG_START_DISTANCE) return;
    dragActive.current = true;
    suppressPress.current = true;
    if (start.subtask) onDragSubtask(start.subtask, pageX, pageY);
    else onDragTask(pageX, pageY);
  }

  function handlePointerEnd(pageX: number, pageY: number): boolean {
    const hadPress = pressStart.current != null;
    const wasDragging = dragActive.current;
    pressStart.current = null;
    dragActive.current = false;
    if (wasDragging) onDragEnd(pageX, pageY);
    return hadPress;
  }

  function handlePress() {
    if (suppressPress.current) {
      suppressPress.current = false;
      return;
    }
    onOpen();
  }

  function rememberSubtask(subtask: ProjectSubtask) {
    if (subtask.subtaskId != null) pressedSubtask.current = subtask;
  }

  // Web gets pointer events (mouse, pen and touch); native gets touch events.
  const cardGestureHandlers =
    Platform.OS === 'web'
      ? {
          onContextMenu: suppressDragMenu,
          onPointerDown: (event: PointerEventLike) => {
            if ((event.nativeEvent.button ?? 0) !== 0) return;
            handlePointerStart(event.nativeEvent.pageX, event.nativeEvent.pageY);
          },
          onPointerMove: (event: PointerEventLike) =>
            handlePointerMove(event.nativeEvent.pageX, event.nativeEvent.pageY),
          onPointerUp: (event: PointerEventLike) => {
            handlePointerEnd(event.nativeEvent.pageX, event.nativeEvent.pageY);
          },
          onPointerCancel: (event: PointerEventLike) => {
            handlePointerEnd(event.nativeEvent.pageX, event.nativeEvent.pageY);
          },
        }
      : {
          onTouchStart: (event: PointerEventLike) => {
            if (
              handlePointerStart(event.nativeEvent.pageX, event.nativeEvent.pageY)
            ) {
              onPressActiveChange(true);
            }
          },
          onTouchMove: (event: PointerEventLike) =>
            handlePointerMove(event.nativeEvent.pageX, event.nativeEvent.pageY),
          onTouchEnd: (event: PointerEventLike) => {
            if (
              handlePointerEnd(event.nativeEvent.pageX, event.nativeEvent.pageY)
            ) {
              onPressActiveChange(false);
            }
          },
          onTouchCancel: (event: PointerEventLike) => {
            if (
              handlePointerEnd(event.nativeEvent.pageX, event.nativeEvent.pageY)
            ) {
              onPressActiveChange(false);
            }
          },
        };

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={task.taskName}
      onPress={handlePress}
      {...cardGestureHandlers}
      style={[
        styles.card,
        dragSafeCardStyle,
        {
          backgroundColor: surfaceColor,
          borderColor: highlighted ? titleColor : borderColor,
        },
        Platform.OS === 'web'
          ? ({
              cursor: canDrag ? (dragging ? 'grabbing' : 'grab') : 'pointer',
            } as ViewStyle)
          : null,
      ]}
    >
      <View style={styles.cardHeader}>
        <Text
          accessible={false}
          selectable={false}
          numberOfLines={2}
          style={[
            styles.cardTitle,
            { color: titleColor, flex: 1 },
            dragSafeTextStyle,
          ]}
        >
          {task.taskName}
        </Text>
        <View style={styles.timerCorner}>
          {(Object.keys(FOCUS_TIMER_PRESETS) as FocusTimerMode[]).map(mode => {
            const preset = FOCUS_TIMER_PRESETS[mode];
            const selected = timerForThisTask?.mode === mode;
            return (
              <Pressable
                key={mode}
                accessibilityRole="button"
                accessibilityLabel={`Start ${preset.label} ${preset.ratioLabel} for ${task.taskName}`}
                hitSlop={4}
                onPress={event => {
                  event.stopPropagation?.();
                  onStartTimer(mode);
                }}
                style={[
                  styles.timerChip,
                  {
                    borderColor: selected ? preset.accent : borderColor,
                    backgroundColor: selected ? preset.accent : preset.chipFill,
                  },
                ]}
              >
                <Text style={styles.timerChipIcon}>{preset.icon}</Text>
                <Text
                  style={[
                    styles.timerChipText,
                    { color: selected ? '#FFFFFF' : titleColor },
                  ]}
                >
                  {preset.ratioLabel}
                </Text>
              </Pressable>
            );
          })}
          {canDelete ? (
            <ConfigurationDeleteButton
              entityLabel={`task ${task.taskName}`}
              placement="inline"
              onPress={onDelete}
            />
          ) : null}
        </View>
      </View>
      {timerForThisTask ? (
        <View style={styles.timerStatus}>
          <Text
            accessibilityLabel={`${timerForThisTask.phaseLabel} ${timerForThisTask.remainingLabel}`}
            style={[styles.timerCountdown, { color: actionColor }]}
          >
            {timerForThisTask.remainingLabel}
          </Text>
          <Text style={[styles.cardMeta, { color: metaColor }]}>
            {FOCUS_TIMER_PRESETS[timerForThisTask.mode].label} ·{' '}
            {timerForThisTask.phaseLabel}
          </Text>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={`Stop timer for ${task.taskName}`}
            onPress={event => {
              event.stopPropagation?.();
              onStopTimer();
            }}
            hitSlop={8}
          >
            <Text style={[styles.timerStop, { color: metaColor }]}>✕ Stop</Text>
          </Pressable>
        </View>
      ) : null}
      {task.completedDate ? (
        <Text
          accessible={false}
          selectable={false}
          style={[styles.cardMeta, { color: metaColor }, dragSafeTextStyle]}
        >
          {formatCompletedCardDate(task.completedDate)}
        </Text>
      ) : null}
      {members ? (
        <Text
          accessible={false}
          selectable={false}
          style={[styles.cardMeta, { color: metaColor }, dragSafeTextStyle]}
        >
          {members}
        </Text>
      ) : null}
      {task.createdByName ? (
        <Text
          accessible={false}
          selectable={false}
          style={[styles.cardMeta, { color: metaColor }, dragSafeTextStyle]}
        >
          Added by {task.createdByName}
        </Text>
      ) : null}
      {task.dueDate && !task.completedDate ? (
        <Text
          accessible={false}
          selectable={false}
          style={[styles.cardMeta, { color: metaColor }, dragSafeTextStyle]}
        >
          {formatCalendarDate(task.dueDate)}
        </Text>
      ) : null}
      {task.timeEstimateMinutes ? (
        <Text
          accessible={false}
          selectable={false}
          style={[styles.cardMeta, { color: metaColor }, dragSafeTextStyle]}
        >
          {formatMinutes(task.timeEstimateMinutes)}
        </Text>
      ) : null}
      {task.completedDate && task.timeSpentMinutes > 0 ? (
        <Text
          accessible={false}
          selectable={false}
          style={[styles.cardMeta, { color: metaColor }, dragSafeTextStyle]}
        >
          Spent {formatMinutes(task.timeSpentMinutes)}
        </Text>
      ) : null}
      {subtasks.length ? (
        <View style={styles.subtaskList}>
          <Text
            accessible={false}
            selectable={false}
            style={[styles.cardMeta, { color: metaColor }, dragSafeTextStyle]}
          >
            Subtasks: {task.subtaskCompleted}/{task.subtaskTotal || subtasks.length}
          </Text>
          {subtasks.map(subtask => (
            <Pressable
              key={String(subtask.subtaskId ?? subtask.subtaskName)}
              ref={view => {
                if (subtask.subtaskId != null) {
                  onSubtaskRef(subtask.subtaskId, view as View | null, task.taskId);
                }
              }}
              accessibilityRole="button"
              accessibilityLabel={subtask.subtaskName}
              onPress={handlePress}
              {...(Platform.OS === 'web'
                ? {
                    onContextMenu: suppressDragMenu,
                    onPointerDown: () => rememberSubtask(subtask),
                  }
                : { onTouchStart: () => rememberSubtask(subtask) })}
              style={[styles.subtaskRow, dragSafeCardStyle]}
            >
              <Text
                accessible={false}
                selectable={false}
                style={[
                  styles.subtaskName,
                  styles.subtaskText,
                  {
                    color: titleColor,
                    textDecorationLine: subtask.isCompleted
                      ? 'line-through'
                      : 'none',
                  },
                  dragSafeTextStyle,
                ]}
              >
                {subtask.isCompleted ? '✓ ' : '• '}
                {subtask.subtaskName}
              </Text>
              {subtask.assignees.length ? (
                <Text
                  accessible={false}
                  selectable={false}
                  numberOfLines={2}
                  style={[
                    styles.cardMeta,
                    styles.subtaskMember,
                    { color: metaColor },
                    dragSafeTextStyle,
                  ]}
                >
                  {assigneeSummary(subtask.assignees)}
                </Text>
              ) : null}
            </Pressable>
          ))}
        </View>
      ) : null}
    </Pressable>
  );
}

export function ProjectTaskBoardScreen({ navigation, route }: Props) {
  const palette = useAppColors();
  const auth = useAuth();
  const capabilities = getProjectModuleCapabilities(auth.permissions, auth.roles);
  const projectId = route.params.projectId;
  // Bumped when a focus session is logged so an open task reloads its time.
  const [timeLogVersion, setTimeLogVersion] = useState(0);
  const { active: activeTimer, start: startTimer, stop: stopTimer } =
    useTaskFocusTimer({
      projectId,
      onSessionLogged: () => {
        setTimeLogVersion(current => current + 1);
        void load(true);
      },
    });
  const [board, setBoard] = useState<ProjectBoard | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [createColumn, setCreateColumn] = useState<ProjectBoardColumn | null>(null);
  const [completeMove, setCompleteMove] = useState<{
    task: ProjectTask;
    column: ProjectBoardColumn;
  } | null>(null);
  const [openTask, setOpenTask] = useState<ProjectTask | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ProjectTask | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [boardWidth, setBoardWidth] = useState(0);
  const [reviewDates, setReviewDates] = useState(false);
  const [reviewDate, setReviewDate] = useState(todayBusinessCalendarDate);
  const [reviewLanes, setReviewLanes] = useState<CompletedFocusBoard | null>(null);
  const [reviewError, setReviewError] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [dropColumnId, setDropColumnId] = useState<number | null>(null);
  // Native: a touch on a draggable card disables scrolling so the drag owns it.
  const [cardPressActive, setCardPressActive] = useState(false);
  // The ghost follows the pointer without re-rendering the board each move.
  const ghostPosition = useRef(new Animated.ValueXY()).current;
  const loadId = useRef(0);
  const reviewLoadId = useRef(0);
  const columnBoxes = useRef(new Map<number, LayoutBox>());
  const subtaskBoxes = useRef(new Map<number, LayoutBox & { taskId: number }>());
  const columnViews = useRef(new Map<number, View | null>());
  const subtaskViews = useRef(new Map<number, View | null>());
  const dragRef = useRef<DragState | null>(null);
  const updateDragRef = useRef<(pageX: number, pageY: number) => void>(() => {});
  const finishDragRef = useRef<(pageX: number, pageY: number) => Promise<void>>(
    async () => {},
  );

  const themed = useMemo(
    () => ({
      loading: { ...typography.body, color: palette.textSecondary },
      columnTitle: {
        ...typography.body,
        color: palette.textPrimary,
        fontWeight: '700' as const,
        flex: 1,
        minWidth: 0,
      },
      dateNav: { ...typography.caption, color: palette.primary, fontWeight: '700' as const },
      dateLabel: {
        ...typography.caption,
        color: palette.textPrimary,
        fontWeight: '700' as const,
      },
      add: {
        color: palette.primary,
        fontSize: 24,
        lineHeight: 26,
        fontWeight: '300' as const,
      },
      count: { ...typography.caption, color: palette.textSecondary },
      groupHeading: {
        ...typography.caption,
        color: palette.textPrimary,
        fontWeight: '700' as const,
        marginTop: spacing.xs,
      },
      hint: { ...typography.caption, color: palette.textSecondary },
      reviewError: { ...typography.caption, color: palette.error },
      column: {
        alignSelf: 'stretch' as const,
        borderWidth: 1,
        borderColor: palette.border,
        backgroundColor: palette.surface,
        borderRadius: 12,
        padding: spacing.sm,
        gap: spacing.sm,
      },
      columnActive: {
        borderColor: palette.primary,
      },
      ghost: {
        ...typography.body,
        color: palette.textPrimary,
        fontWeight: '700' as const,
      },
    }),
    [palette],
  );

  const load = useCallback(
    async (refresh = false) => {
      const current = ++loadId.current;
      if (refresh) setRefreshing(true);
      else setLoading(true);
      try {
        const next = await getProjectBoard(projectId);
        if (current !== loadId.current) return;
        setBoard(next);
        setError('');
      } catch (caught) {
        if (current !== loadId.current) return;
        setError(projectErrorMessage(caught, 'Unable to load the project board.'));
      } finally {
        if (current === loadId.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [projectId],
  );

  const loadReview = useCallback(
    async (date: string, refresh = false) => {
      const current = ++reviewLoadId.current;
      if (refresh) setRefreshing(true);
      else setReviewLoading(true);
      try {
        const next = await getCompletedFocusTasks(projectId, date);
        if (current !== reviewLoadId.current) return;
        setReviewLanes(next);
        setReviewError('');
      } catch (caught) {
        if (current !== reviewLoadId.current) return;
        setReviewError(
          projectErrorMessage(caught, 'Unable to load completed tasks.'),
        );
      } finally {
        if (current === reviewLoadId.current) {
          setReviewLoading(false);
          setRefreshing(false);
        }
      }
    },
    [projectId],
  );

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load]),
  );

  useEffect(() => {
    if (!reviewDates) return;
    void loadReview(reviewDate);
  }, [loadReview, reviewDate, reviewDates]);

  const displayColumns: DisplayColumn[] = useMemo(() => {
    if (!board) return [];
    if (!reviewDates) return board.columns;
    const todayTasks = reviewLanes?.todayTasks ?? [];
    const yesterdayTasks = reviewLanes?.yesterdayTasks ?? [];
    return [
      {
        projectColumnId: -1,
        columnName: 'Today',
        displayOrder: 1,
        taskLimit: null,
        isCompletedColumn: false,
        isDefaultColumn: false,
        requiresTaskDetails: true,
        taskCount: todayTasks.length,
        tasks: todayTasks,
        reviewLane: 'today',
      },
      {
        projectColumnId: -2,
        columnName: 'Yesterday',
        displayOrder: 2,
        taskLimit: null,
        isCompletedColumn: false,
        isDefaultColumn: false,
        requiresTaskDetails: true,
        taskCount: yesterdayTasks.length,
        tasks: yesterdayTasks,
        reviewLane: 'yesterday',
      },
    ];
  }, [board, reviewDates, reviewLanes]);

  const todayCalendarDate = todayBusinessCalendarDate();
  const canAdvanceReviewDate = reviewDate < todayCalendarDate;

  function measureViews() {
    columnViews.current.forEach((view, columnId) => {
      view?.measureInWindow((x, y, width, height) => {
        // Ignore empty frames (common in unit tests) so onLayout seeds remain.
        if (width <= 0 || height <= 0) return;
        columnBoxes.current.set(columnId, { x, y, width, height });
      });
    });
    subtaskViews.current.forEach((view, subtaskId) => {
      view?.measureInWindow((x, y, width, height) => {
        if (width <= 0 || height <= 0) return;
        const existing = subtaskBoxes.current.get(subtaskId);
        subtaskBoxes.current.set(subtaskId, {
          x,
          y,
          width,
          height,
          taskId: existing?.taskId ?? 0,
        });
      });
    });
  }

  function columnAt(pageX: number, pageY: number): number | null {
    for (const [columnId, box] of columnBoxes.current) {
      if (
        pageX >= box.x &&
        pageX <= box.x + box.width &&
        pageY >= box.y &&
        pageY <= box.y + box.height
      ) {
        return columnId;
      }
    }
    return null;
  }

  function requestCreate(column: ProjectBoardColumn) {
    if (!capabilities.canManageTasks || !column.projectColumnId) return;
    if (
      !isUnlimitedTaskLimit(column.taskLimit) &&
      column.taskLimit != null &&
      column.taskCount >= column.taskLimit
    ) {
      Alert.alert(
        'Task limit reached',
        `${column.columnName} already has ${column.taskCount} / ${column.taskLimit} tasks.`,
      );
      return;
    }
    setCreateColumn(column);
  }

  async function dropTask(task: ProjectTask, pageX: number, pageY: number) {
    if (!board || !capabilities.canManageTasks) return;
    const columnId = columnAt(pageX, pageY);
    const column = board.columns.find(item => item.projectColumnId === columnId);
    if (!column) return;
    const plan = planTaskMove({
      currentColumnId: task.projectColumnId,
      column,
      assignedEmployeeId: task.assignedEmployeeId,
      assignedEmployeeIds: task.assignees.map(item => item.employeeId),
      dueDate: task.dueDate ?? '',
      timeEstimate: formatDurationInput(task.timeEstimateMinutes),
    });
    if (plan.kind === 'noop') return;
    if (plan.kind === 'blocked') {
      Alert.alert(plan.title, plan.message);
      return;
    }
    if (plan.kind === 'promptComplete') {
      setCompleteMove({ task, column });
      return;
    }
    const projectColumnId = column.projectColumnId;
    if (!projectColumnId) return;
    const previousBoard = board;
    setBoard(relocateTaskOnBoard(board, task.taskId, projectColumnId));
    try {
      await runOperation(
        'update',
        () =>
          moveProjectTask(projectId, task.taskId, projectColumnId, {
            completedDate: null,
          }),
        {
          failureDetail: caught =>
            projectErrorMessage(caught, 'The task could not be moved.'),
        },
      );
      await load(true);
    } catch {
      // runOperation already showed the "Update failed" popup.
      setBoard(previousBoard);
    }
  }

  async function dropSubtask(
    task: ProjectTask,
    subtask: ProjectSubtask,
    pageY: number,
  ) {
    if (!capabilities.canManageTasks || subtask.subtaskId == null) return;
    const ordered = [...task.subtasks]
      .filter(item => item.subtaskId != null)
      .sort((left, right) => left.displayOrder - right.displayOrder);
    const ids = ordered.map(item => item.subtaskId as number);
    const without = ids.filter(id => id !== subtask.subtaskId);
    let insertAt = without.length;
    for (let index = 0; index < without.length; index += 1) {
      const box = subtaskBoxes.current.get(without[index]);
      if (!box || box.taskId !== task.taskId) continue;
      if (pageY < box.y + box.height / 2) {
        insertAt = index;
        break;
      }
    }
    without.splice(insertAt, 0, subtask.subtaskId);
    if (without.join(',') === ids.join(',')) return;
    try {
      await runOperation(
        'update',
        () => reorderProjectSubtasks(projectId, task.taskId, without),
        {
          failureDetail: caught =>
            projectErrorMessage(caught, 'The subtasks could not be reordered.'),
        },
      );
      await load(true);
    } catch {
      // runOperation already showed the "Update failed" popup.
    }
  }

  function moveGhost(pageX: number, pageY: number) {
    ghostPosition.setValue({
      x: pageX - GHOST_OFFSET.x,
      y: pageY - GHOST_OFFSET.y,
    });
  }

  function startDrag(next: DragState, pageX: number, pageY: number) {
    measureViews();
    moveGhost(pageX, pageY);
    dragRef.current = next;
    setDrag(next);
    setDropColumnId(next.kind === 'task' ? columnAt(pageX, pageY) : null);
  }

  function updateDrag(pageX: number, pageY: number) {
    const current = dragRef.current;
    if (!current) return;
    moveGhost(pageX, pageY);
    if (current.kind === 'task') setDropColumnId(columnAt(pageX, pageY));
  }

  async function finishDrag(pageX: number, pageY: number) {
    const current = dragRef.current;
    dragRef.current = null;
    setDrag(null);
    setDropColumnId(null);
    if (!current) return;
    if (current.kind === 'task') {
      await dropTask(current.task, pageX, pageY);
      return;
    }
    await dropSubtask(current.task, current.subtask, pageY);
  }

  updateDragRef.current = updateDrag;
  finishDragRef.current = finishDrag;

  // Web: keep tracking the drag even when the pointer leaves the card,
  // and block the browser context menu for the whole drag gesture.
  useEffect(() => {
    if (drag == null || Platform.OS !== 'web') return undefined;
    const onMove = (event: { clientX: number; clientY: number }) => {
      updateDragRef.current(event.clientX, event.clientY);
    };
    const onUp = (event: { clientX: number; clientY: number }) => {
      void finishDragRef.current(event.clientX, event.clientY);
    };
    const blockMenu = (event: { preventDefault: () => void }) => {
      event.preventDefault();
    };
    const target = globalThis as typeof globalThis & {
      addEventListener?: (
        type: string,
        listener: (event: never) => void,
        options?: boolean,
      ) => void;
      removeEventListener?: (
        type: string,
        listener: (event: never) => void,
        options?: boolean,
      ) => void;
    };
    target.addEventListener?.('pointermove', onMove as never);
    target.addEventListener?.('pointerup', onUp as never);
    target.addEventListener?.('pointercancel', onUp as never);
    target.addEventListener?.('contextmenu', blockMenu as never, true);
    return () => {
      target.removeEventListener?.('pointermove', onMove as never);
      target.removeEventListener?.('pointerup', onUp as never);
      target.removeEventListener?.('pointercancel', onUp as never);
      target.removeEventListener?.('contextmenu', blockMenu as never, true);
    };
  }, [drag]);

  function taskDeleted(taskId: number) {
    setOpenTask(current => (current?.taskId === taskId ? null : current));
    setBoard(current => (current ? removeTaskFromBoard(current, taskId) : current));
    void load(true);
    if (reviewDates) void loadReview(reviewDate, true);
  }

  async function deleteTask(task: ProjectTask) {
    if (!capabilities.canManageTasks || deleting) return;
    setDeleting(true);
    try {
      await runOperation('delete', () => deleteProjectTask(projectId, task.taskId), {
        failureDetail: caught =>
          projectErrorMessage(caught, 'The task could not be deleted.'),
      });
      taskDeleted(task.taskId);
    } catch {
      // runOperation already showed the "Delete failed" popup.
    } finally {
      setDeleting(false);
      setPendingDelete(null);
    }
  }

  const columnWidth = boardColumnWidth(
    boardWidth,
    displayColumns.length,
    spacing.md,
  );

  const title = board?.projectName || route.params.projectName || 'Project';

  return (
    <PermissionGuard
      permission={PERMISSIONS.PROJECT_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen contentStyle={styles.screen} webLayout="fluid">
        <ScreenHeader
          title={title}
          onBack={navigation.goBack}
          right={
            <View style={styles.headerActions}>
              {capabilities.canViewTaskReport ? (
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={`Task report for ${title}`}
                  onPress={() =>
                    navigation.navigate(routes.projectReport, {
                      projectId,
                      projectName: title,
                    })
                  }
                  hitSlop={8}
                  style={[styles.reportButton, { borderColor: palette.white }]}
                >
                  <Text style={[styles.reportButtonText, { color: palette.white }]}>
                    Report
                  </Text>
                </Pressable>
              ) : null}
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={
                  reviewDates
                    ? 'Show full board'
                    : 'Show today and yesterday tasks'
                }
                accessibilityState={{ selected: reviewDates }}
                onPress={() => {
                  setReviewDates(open => {
                    if (!open) setReviewDate(todayBusinessCalendarDate());
                    return !open;
                  });
                }}
                hitSlop={8}
              >
                <EyeIcon color={palette.white} active={reviewDates} />
              </Pressable>
            </View>
          }
        />
        {loading && !board ? (
          <View style={styles.center}>
            <Text style={themed.loading}>Loading board…</Text>
          </View>
        ) : error && !board ? (
          <ErrorState message={error} onRetry={() => load()} />
        ) : !board || board.columns.length === 0 ? (
          <EmptyState
            title="No columns were found."
            message="This project does not have a board configuration yet."
            actionTitle="Retry"
            onAction={() => load()}
          />
        ) : (
          <View
            testID="board-viewport"
            style={styles.flex}
            onLayout={event => setBoardWidth(event.nativeEvent.layout.width)}
          >
            {/* The page scrolls vertically (with pull-to-refresh); the board
                row scrolls horizontally inside it, so the two never compete. */}
            <ScrollView
              testID="board-vertical-scroll"
              style={styles.flex}
              contentContainerStyle={styles.page}
              scrollEnabled={drag == null && !cardPressActive}
              refreshControl={
                <RefreshControl
                  refreshing={refreshing}
                  onRefresh={() =>
                    reviewDates ? loadReview(reviewDate, true) : load(true)
                  }
                />
              }
            >
              <Text style={[themed.hint, styles.hint]}>
                {reviewDates
                  ? 'Today and Yesterday show tasks completed on the selected date and the day before.'
                  : 'Tap a task for details. Drag tasks between columns or drag subtasks to reorder them.'}
              </Text>
              {reviewDates && reviewLoading && !reviewLanes ? (
                <Text style={[themed.hint, styles.hint]}>Loading completed tasks…</Text>
              ) : null}
              {reviewDates && reviewError ? (
                <Text style={[themed.reviewError, styles.hint]}>{reviewError}</Text>
              ) : null}
              {reviewDates ? (
                <View style={[styles.board, styles.focusBoard]}>
                  {displayColumns.map(column => (
                    <View
                      key={String(column.projectColumnId)}
                      testID={
                        column.projectColumnId != null && column.projectColumnId > 0
                          ? `board-column-${column.projectColumnId}`
                          : undefined
                      }
                      ref={view => {
                        if (
                          !reviewDates &&
                          column.projectColumnId != null &&
                          column.projectColumnId > 0
                        ) {
                          if (view) {
                            (view as BoardColumnView)._boardColumnId =
                              column.projectColumnId;
                          }
                          columnViews.current.set(column.projectColumnId, view);
                        }
                      }}
                      onLayout={event => {
                        if (
                          !reviewDates &&
                          column.projectColumnId != null &&
                          column.projectColumnId > 0
                        ) {
                          const { x, y, width, height } = event.nativeEvent.layout;
                          if (width > 0 && height > 0) {
                            columnBoxes.current.set(column.projectColumnId, {
                              x,
                              y,
                              width,
                              height,
                            });
                          }
                        }
                        measureViews();
                      }}
                      style={[
                        themed.column,
                        reviewDates ? styles.focusColumn : { width: columnWidth },
                        dropColumnId === column.projectColumnId
                          ? themed.columnActive
                          : null,
                      ]}
                    >
                      <View style={styles.columnHeader}>
                        <Text style={themed.columnTitle} numberOfLines={1}>
                          {column.columnName}
                        </Text>
                        {column.reviewLane === 'today' ? (
                          <View style={styles.dateNav}>
                            <Pressable
                              accessibilityRole="button"
                              accessibilityLabel="Previous date"
                              onPress={() =>
                                setReviewDate(current =>
                                  shiftCompletedFocusDate(current, -1),
                                )
                              }
                              hitSlop={8}
                            >
                              <Text style={themed.dateNav}>‹</Text>
                            </Pressable>
                            <Text
                              accessibilityLabel={`Selected date ${formatCompletedFocusDate(reviewDate)}`}
                              style={themed.dateLabel}
                              numberOfLines={1}
                            >
                              {formatCompletedFocusDate(reviewDate)}
                            </Text>
                            <Pressable
                              accessibilityRole="button"
                              accessibilityLabel="Next date"
                              disabled={!canAdvanceReviewDate}
                              onPress={() =>
                                setReviewDate(current =>
                                  shiftCompletedFocusDate(current, 1),
                                )
                              }
                              hitSlop={8}
                            >
                              <Text
                                style={[
                                  themed.dateNav,
                                  !canAdvanceReviewDate
                                    ? { opacity: 0.35 }
                                    : null,
                                ]}
                              >
                                ›
                              </Text>
                            </Pressable>
                          </View>
                        ) : null}
                        {capabilities.canManageTasks && !reviewDates ? (
                          <Pressable
                            accessibilityRole="button"
                            accessibilityLabel={`Add task to ${column.columnName}`}
                            onPress={() => requestCreate(column)}
                            hitSlop={8}
                          >
                            <Text style={themed.add}>+</Text>
                          </Pressable>
                        ) : null}
                      </View>
                      <Text style={themed.count}>
                        {formatColumnTaskCount(column.taskCount, column.taskLimit)}
                      </Text>
                      <View style={styles.columnBody}>
                        {(column.isCompletedColumn
                          ? groupTasksByCompletedDate(column.tasks)
                          : [{ date: '', heading: '', tasks: column.tasks }]
                        ).map(group => (
                          <View key={group.date || 'open'} style={styles.taskGroup}>
                            {group.heading ? (
                              <Text style={themed.groupHeading}>{group.heading}</Text>
                            ) : null}
                            {group.tasks.map(task => (
                              <TaskCard
                                key={task.taskId}
                                task={task}
                                titleColor={palette.textPrimary}
                                metaColor={palette.textSecondary}
                                borderColor={palette.border}
                                surfaceColor={palette.background}
                                actionColor={palette.primary}
                                canDrag={capabilities.canManageTasks && !reviewDates}
                                canDelete={capabilities.canManageTasks && !reviewDates}
                                highlighted={drag?.task.taskId === task.taskId}
                                dragging={drag?.task.taskId === task.taskId}
                                activeTimer={activeTimer}
                                onOpen={() => setOpenTask(task)}
                                onDelete={() => setPendingDelete(task)}
                                onDragTask={(pageX, pageY) =>
                                  startDrag({ kind: 'task', task }, pageX, pageY)
                                }
                                onDragMove={(pageX, pageY) => {
                                  updateDrag(pageX, pageY);
                                }}
                                onDragEnd={(pageX, pageY) => {
                                  void finishDrag(pageX, pageY);
                                }}
                                onDragSubtask={(subtask, pageX, pageY) =>
                                  startDrag(
                                    { kind: 'subtask', task, subtask },
                                    pageX,
                                    pageY,
                                  )
                                }
                                onPressActiveChange={setCardPressActive}
                                onSubtaskRef={(subtaskId, view, parentTaskId) => {
                                  subtaskViews.current.set(subtaskId, view);
                                  const existing = subtaskBoxes.current.get(subtaskId);
                                  subtaskBoxes.current.set(subtaskId, {
                                    x: existing?.x ?? 0,
                                    y: existing?.y ?? 0,
                                    width: existing?.width ?? 0,
                                    height: existing?.height ?? 24,
                                    taskId: parentTaskId,
                                  });
                                }}
                                onStartTimer={mode => startTimer(task.taskId, mode)}
                                onStopTimer={stopTimer}
                              />
                            ))}
                            {reviewDates && group.tasks.length === 0 ? (
                              <Text style={themed.hint}>No completed tasks.</Text>
                            ) : null}
                          </View>
                        ))}
                      </View>
                    </View>
                  ))}
                </View>
              ) : (
                <ScrollView
                  testID="board-horizontal-scroll"
                  horizontal
                  nestedScrollEnabled
                  showsHorizontalScrollIndicator
                  scrollEnabled={drag == null && !cardPressActive}
                  contentContainerStyle={styles.board}
                >
                  {displayColumns.map(column => (
                    <View
                      key={String(column.projectColumnId)}
                      testID={
                        column.projectColumnId != null && column.projectColumnId > 0
                          ? `board-column-${column.projectColumnId}`
                          : undefined
                      }
                      ref={view => {
                        if (
                          !reviewDates &&
                          column.projectColumnId != null &&
                          column.projectColumnId > 0
                        ) {
                          if (view) {
                            (view as BoardColumnView)._boardColumnId =
                              column.projectColumnId;
                          }
                          columnViews.current.set(column.projectColumnId, view);
                        }
                      }}
                      onLayout={event => {
                        if (
                          !reviewDates &&
                          column.projectColumnId != null &&
                          column.projectColumnId > 0
                        ) {
                          const { x, y, width, height } = event.nativeEvent.layout;
                          if (width > 0 && height > 0) {
                            columnBoxes.current.set(column.projectColumnId, {
                              x,
                              y,
                              width,
                              height,
                            });
                          }
                        }
                        measureViews();
                      }}
                      style={[
                        themed.column,
                        reviewDates ? styles.focusColumn : { width: columnWidth },
                        dropColumnId === column.projectColumnId
                          ? themed.columnActive
                          : null,
                      ]}
                    >
                      <View style={styles.columnHeader}>
                        <Text style={themed.columnTitle} numberOfLines={1}>
                          {column.columnName}
                        </Text>
                        {column.reviewLane === 'today' ? (
                          <View style={styles.dateNav}>
                            <Pressable
                              accessibilityRole="button"
                              accessibilityLabel="Previous date"
                              onPress={() =>
                                setReviewDate(current =>
                                  shiftCompletedFocusDate(current, -1),
                                )
                              }
                              hitSlop={8}
                            >
                              <Text style={themed.dateNav}>‹</Text>
                            </Pressable>
                            <Text
                              accessibilityLabel={`Selected date ${formatCompletedFocusDate(reviewDate)}`}
                              style={themed.dateLabel}
                              numberOfLines={1}
                            >
                              {formatCompletedFocusDate(reviewDate)}
                            </Text>
                            <Pressable
                              accessibilityRole="button"
                              accessibilityLabel="Next date"
                              disabled={!canAdvanceReviewDate}
                              onPress={() =>
                                setReviewDate(current =>
                                  shiftCompletedFocusDate(current, 1),
                                )
                              }
                              hitSlop={8}
                            >
                              <Text
                                style={[
                                  themed.dateNav,
                                  !canAdvanceReviewDate
                                    ? { opacity: 0.35 }
                                    : null,
                                ]}
                              >
                                ›
                              </Text>
                            </Pressable>
                          </View>
                        ) : null}
                        {capabilities.canManageTasks && !reviewDates ? (
                          <Pressable
                            accessibilityRole="button"
                            accessibilityLabel={`Add task to ${column.columnName}`}
                            onPress={() => requestCreate(column)}
                            hitSlop={8}
                          >
                            <Text style={themed.add}>+</Text>
                          </Pressable>
                        ) : null}
                      </View>
                      <Text style={themed.count}>
                        {formatColumnTaskCount(column.taskCount, column.taskLimit)}
                      </Text>
                      <View style={styles.columnBody}>
                        {(column.isCompletedColumn
                          ? groupTasksByCompletedDate(column.tasks)
                          : [{ date: '', heading: '', tasks: column.tasks }]
                        ).map(group => (
                          <View key={group.date || 'open'} style={styles.taskGroup}>
                            {group.heading ? (
                              <Text style={themed.groupHeading}>{group.heading}</Text>
                            ) : null}
                            {group.tasks.map(task => (
                              <TaskCard
                                key={task.taskId}
                                task={task}
                                titleColor={palette.textPrimary}
                                metaColor={palette.textSecondary}
                                borderColor={palette.border}
                                surfaceColor={palette.background}
                                actionColor={palette.primary}
                                canDrag={capabilities.canManageTasks && !reviewDates}
                                canDelete={capabilities.canManageTasks && !reviewDates}
                                highlighted={drag?.task.taskId === task.taskId}
                                dragging={drag?.task.taskId === task.taskId}
                                activeTimer={activeTimer}
                                onOpen={() => setOpenTask(task)}
                                onDelete={() => setPendingDelete(task)}
                                onDragTask={(pageX, pageY) =>
                                  startDrag({ kind: 'task', task }, pageX, pageY)
                                }
                                onDragMove={(pageX, pageY) => {
                                  updateDrag(pageX, pageY);
                                }}
                                onDragEnd={(pageX, pageY) => {
                                  void finishDrag(pageX, pageY);
                                }}
                                onDragSubtask={(subtask, pageX, pageY) =>
                                  startDrag(
                                    { kind: 'subtask', task, subtask },
                                    pageX,
                                    pageY,
                                  )
                                }
                                onPressActiveChange={setCardPressActive}
                                onSubtaskRef={(subtaskId, view, parentTaskId) => {
                                  subtaskViews.current.set(subtaskId, view);
                                  const existing = subtaskBoxes.current.get(subtaskId);
                                  subtaskBoxes.current.set(subtaskId, {
                                    x: existing?.x ?? 0,
                                    y: existing?.y ?? 0,
                                    width: existing?.width ?? 0,
                                    height: existing?.height ?? 24,
                                    taskId: parentTaskId,
                                  });
                                }}
                                onStartTimer={mode => startTimer(task.taskId, mode)}
                                onStopTimer={stopTimer}
                              />
                            ))}
                            {reviewDates && group.tasks.length === 0 ? (
                              <Text style={themed.hint}>No completed tasks.</Text>
                            ) : null}
                          </View>
                        ))}
                      </View>
                    </View>
                  ))}
                </ScrollView>
              )}
            </ScrollView>
          </View>
        )}
        {drag ? (
          <View
            testID="task-drag-layer"
            style={styles.dragLayer}
            onStartShouldSetResponder={() => true}
            onMoveShouldSetResponder={() => true}
            onResponderMove={event =>
              updateDrag(event.nativeEvent.pageX, event.nativeEvent.pageY)
            }
            onResponderRelease={event =>
              void finishDrag(event.nativeEvent.pageX, event.nativeEvent.pageY)
            }
            onResponderTerminate={event =>
              void finishDrag(event.nativeEvent.pageX, event.nativeEvent.pageY)
            }
            {...(Platform.OS === 'web'
              ? { onContextMenu: suppressDragMenu }
              : {})}
          >
            <Animated.View
              pointerEvents="none"
              style={[
                styles.ghost,
                ghostPosition.getLayout(),
                {
                  backgroundColor: palette.surface,
                  borderColor: palette.primary,
                },
              ]}
            >
              <Text style={themed.ghost}>
                {drag.kind === 'subtask'
                  ? drag.subtask.subtaskName
                  : drag.task.taskName}
              </Text>
            </Animated.View>
          </View>
        ) : null}
        <ConfirmDialog
          visible={pendingDelete != null}
          title="Confirm to delete?"
          destructive
          busy={deleting}
          onConfirm={() => {
            if (pendingDelete) void deleteTask(pendingDelete);
          }}
          onCancel={() => setPendingDelete(null)}
        />
        <CreateTaskModal
          visible={createColumn != null}
          projectId={projectId}
          column={createColumn}
          members={board?.members ?? []}
          onClose={() => setCreateColumn(null)}
          onCreated={() => {
            setCreateColumn(null);
            void load(true);
            if (reviewDates) void loadReview(reviewDate, true);
          }}
        />
        <CompleteTaskModal
          visible={completeMove != null}
          projectId={projectId}
          column={completeMove?.column ?? null}
          members={board?.members ?? []}
          task={completeMove?.task ?? null}
          onClose={() => setCompleteMove(null)}
          onCompleted={() => {
            setCompleteMove(null);
            void load(true);
            if (reviewDates) void loadReview(reviewDate, true);
          }}
        />
        <Modal
          visible={openTask != null}
          transparent
          animationType="fade"
          onRequestClose={() => setOpenTask(null)}
        >
          <View style={styles.detailsOverlay} pointerEvents="box-none">
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Close task details"
              style={styles.detailsBackdrop}
              onPress={() => setOpenTask(null)}
            />
            {openTask ? (
              <View style={styles.detailsCard} pointerEvents="box-none">
                <TaskDetailsPanel
                  projectId={projectId}
                  taskId={openTask.taskId}
                  initialTask={openTask}
                  members={board?.members ?? []}
                  columns={board?.columns ?? []}
                  canEdit={capabilities.canManageTasks}
                  onClose={() => setOpenTask(null)}
                  onTaskChanged={() => {
                    void load(true);
                    if (reviewDates) void loadReview(reviewDate, true);
                  }}
                  onDeleted={taskDeleted}
                  activeTimer={activeTimer}
                  onStartTimer={mode => startTimer(openTask.taskId, mode)}
                  onStopTimer={stopTimer}
                  timeLogVersion={timeLogVersion}
                />
              </View>
            ) : null}
          </View>
          {openTask ? <OperationFeedbackHost /> : null}
        </Modal>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, paddingBottom: 0 },
  headerActions: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  reportButton: {
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
  },
  reportButtonText: { ...typography.caption, fontWeight: '600' },
  flex: { flex: 1 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  hint: { paddingTop: spacing.sm },
  page: { flexGrow: 1, paddingBottom: spacing.xl },
  board: {
    flexGrow: 1,
    alignItems: 'stretch',
    paddingTop: spacing.md,
    gap: spacing.md,
  },
  focusBoard: {
    flexGrow: 1,
    width: '100%',
    flexDirection: 'row',
  },
  focusColumn: {
    flex: 1,
    width: undefined,
    minWidth: 0,
  },
  columnHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  dateNav: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 'auto',
    gap: 6,
    flexShrink: 0,
  },
  eyeRoot: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  eyeLid: {
    width: 20,
    height: 12,
    borderRadius: 10,
    borderWidth: 2,
  },
  eyePupil: {
    position: 'absolute',
    width: 5,
    height: 5,
    borderRadius: 3,
  },
  eyeSlash: {
    position: 'absolute',
    width: 18,
    height: 2,
    borderRadius: 1,
    transform: [{ rotate: '-28deg' }],
  },
  columnBody: { gap: spacing.sm, paddingBottom: spacing.lg },
  taskGroup: { gap: spacing.sm },
  card: {
    borderWidth: 1,
    borderRadius: 10,
    padding: spacing.md,
    gap: 4,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  cardTitle: { ...typography.body, fontWeight: '600' },
  cardMeta: { ...typography.caption },
  timerCorner: {
    alignItems: 'flex-end',
    gap: 4,
  },
  timerChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 6,
    paddingVertical: 3,
  },
  timerChipIcon: {
    fontSize: 12,
    lineHeight: 14,
    ...Platform.select({
      web: {
        fontFamily:
          '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif',
      },
      default: {},
    }),
  },
  timerChipText: {
    fontWeight: '700',
    fontSize: 11,
    lineHeight: 14,
  },
  timerStatus: {
    gap: 2,
    marginBottom: spacing.xs,
  },
  timerCountdown: {
    ...typography.sectionTitle,
    fontVariant: ['tabular-nums'],
  },
  timerStop: {
    ...typography.caption,
    fontWeight: '700',
    marginTop: 2,
  },
  subtaskList: { gap: 4, marginTop: spacing.xs },
  subtaskName: { ...typography.caption, fontWeight: '600' },
  // Subtask text and its members share a row; the text wraps in its space.
  subtaskRow: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.xs },
  subtaskText: { flex: 1, minWidth: 0 },
  subtaskMember: { maxWidth: '45%', textAlign: 'right' },
  dragLayer: {
    ...StyleSheet.absoluteFill,
    zIndex: 20,
  },
  ghost: {
    position: 'absolute',
    minWidth: 160,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  detailsOverlay: {
    flex: 1,
    backgroundColor: 'rgba(12, 17, 29, 0.55)',
    justifyContent: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xl,
  },
  detailsBackdrop: {
    ...StyleSheet.absoluteFill,
  },
  // Shrinks to the overlay height so the task panel inside can scroll.
  detailsCard: {
    zIndex: 2,
    flexShrink: 1,
    minHeight: 0,
    width: '100%',
  },
});
